// components/common/Modal/TableRangeDialog.tsx
import { useState, useEffect, ReactElement, ChangeEvent } from 'react';
import { Box, TextField } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Modal } from './Modal';

export interface TableRangeDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (startValue: string, endValue: string) => void;
  title?: string;
  defaultStart?: string;
  defaultEnd?: string;
  loading?: boolean;
}

const buttonBaseStyle: SxProps<Theme> = {
  px: 3,
  py: 1,
  fontFamily: '"Rajdhani", sans-serif',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
};

const textFieldStyle: SxProps<Theme> = {
  mt: 2,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#252D3A',
    color: '#FFFFFF',
    height: 56,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#00FFD4' },
  },
  '& .MuiInputLabel-root': {
    color: '#8892A0',
    backgroundColor: '#1E2530',
    px: 1,
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#00FFD4' },
};

/**
 * TableRangeDialog - Dialog for entering table range (start and end)
 */
export function TableRangeDialog({
  open,
  onClose,
  onSubmit,
  title = 'Set Table Range',
  defaultStart = '',
  defaultEnd = '',
  loading = false,
}: TableRangeDialogProps): ReactElement {
  const [startValue, setStartValue] = useState(defaultStart);
  const [endValue, setEndValue] = useState(defaultEnd);

  useEffect(() => {
    if (open) {
      setStartValue(defaultStart);
      setEndValue(defaultEnd);
    }
  }, [open, defaultStart, defaultEnd]);

  const handleSubmit = () => {
    onSubmit(startValue, endValue);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      titleColor="#00FFD4"
      maxWidth="sm"
      showCloseButton={false}
      actions={
        <>
          <Box
            component="button"
            onClick={onClose}
            disabled={loading}
            sx={{
              ...buttonBaseStyle,
              backgroundColor: 'transparent',
              border: 'none',
              color: '#8892A0',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#FFFFFF' },
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              ...buttonBaseStyle,
              backgroundColor: 'rgba(0, 255, 212, 0.15)',
              border: '1px solid #00FFD4',
              color: '#00FFD4',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 255, 212, 0.25)',
                boxShadow: '0 4px 15px rgba(0, 255, 212, 0.4)',
              },
            }}
          >
            {loading ? 'Processing...' : 'Set Range'}
          </Box>
        </>
      }
    >
      <Box sx={{ display: 'flex', gap: 3 }}>
        <TextField
          autoFocus
          fullWidth
          label="Starting Table"
          type="number"
          value={startValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setStartValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={textFieldStyle}
        />
        <TextField
          fullWidth
          label="Ending Table"
          type="number"
          value={endValue}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setEndValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={textFieldStyle}
        />
      </Box>
    </Modal>
  );
}

export default TableRangeDialog;
